import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Mail, Trash2, Clock, ShieldCheck, Link2 } from 'lucide-react';
import { api } from '@/api';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', desc: 'Full access — including managing other admins.' },
  { value: 'admin',       label: 'Admin',       desc: 'Full content access. Cannot manage other admins.' },
  { value: 'editor',      label: 'Editor',      desc: 'Content only — Games, Teams, Media, News. No Ads, Settings, or Users.' },
  { value: 'viewer',      label: 'Viewer',      desc: 'Read-only — can view admin pages but cannot change anything.' },
];
const ROLE_BADGE = {
  super_admin: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  admin:       'bg-sky-500/20 text-sky-300 border-sky-500/40',
  editor:      'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  viewer:      'bg-gray-500/20 text-gray-300 border-gray-500/40',
};


const InviteDialog = ({ open, onClose, onInvited }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setEmail(''); setName(''); setRole('admin'); setBusy(false); setError(''); }
  }, [open]);

  const submit = async () => {
    if (!email || !name) { setError('Email and name are required.'); return; }
    setBusy(true); setError('');
    try {
      const res = await api.inviteAdmin({ email: email.trim().toLowerCase(), name: name.trim(), role });
      onInvited(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invite failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md bg-gray-900 border-sky-500/40">
        <DialogHeader>
          <DialogTitle className="text-white">Invite a New Admin</DialogTitle>
          <DialogDescription className="text-gray-400">
            We'll email them a link to set their own password and activate their account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-gray-300">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="newadmin@wpsl.tv"
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Name</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jordan Rivera"
              className="bg-gray-700 border-gray-600 text-white"
              data-testid="invite-name-input"
            />
          </div>
          <div>
            <Label className="text-gray-300">Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-2"
              data-testid="invite-role-select"
            >
              {ROLES.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{ROLES.find(r => r.value === role)?.desc}</p>
          </div>
          {error && (
            <div className="bg-red-500/20 border border-red-500/40 text-red-300 px-3 py-2 rounded text-sm" data-testid="invite-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-gray-600 text-gray-300" onClick={() => { if (!busy) onClose(); }} disabled={busy} data-testid="invite-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-sky-400 hover:bg-sky-500 text-gray-900" data-testid="invite-submit">
              {busy ? 'Sending…' : 'Send Invitation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export const AdminUsers = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState(null);

  const isSuper = currentUser?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAdminUsers();
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to load admins', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRoleChange = async (user, newRole) => {
    if (newRole === user.role) return;
    setBusy(user.id);
    try {
      await api.updateAdminRole(user.id, newRole);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not update role');
    } finally {
      setBusy(null);
    }
  };

  const onResend = async (user) => {
    setBusy(user.id);
    try {
      await api.resendInvite(user.id);
      alert(`Invitation re-sent to ${user.email}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setBusy(null);
    }
  };

  const onCopyManualLink = async (user) => {
    setBusy(user.id);
    try {
      const res = await api.manualInviteLink(user.id);
      const url = res.data?.url;
      if (!url) throw new Error('No invite URL returned');
      try {
        await navigator.clipboard.writeText(url);
        alert(
          `Invite link copied to clipboard for ${user.email}\n\n${url}\n\n` +
          `Expires in ${res.data.expires_in_days} day(s). Send this link to the invitee directly ` +
          `(e.g. via Slack/SMS). They'll set their own password on /setup.`
        );
      } catch {
        // Clipboard API blocked (e.g. http context) — show the link in a prompt so user can copy manually
        window.prompt('Copy this invite link (clipboard blocked):', url);
      }
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Failed to generate invite link');
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (user) => {
    if (!window.confirm(`Permanently remove ${user.email} from the admin panel?`)) return;
    setBusy(user.id);
    try {
      await api.deleteAdminUser(user.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete user');
    } finally {
      setBusy(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <InviteDialog open={showInvite} onClose={() => setShowInvite(false)} onInvited={() => load()} />

      <div className="flex flex-wrap justify-between items-center gap-2">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Admin Users ({users.length})
          </h2>
          <p className="text-sm text-gray-400">Invite teammates and manage what they can do.</p>
        </div>
        {isSuper && (
          <Button
            onClick={() => setShowInvite(true)}
            className="bg-sky-400 hover:bg-sky-500 text-gray-900 rounded-full"
            data-testid="invite-admin-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Invite Admin
          </Button>
        )}
      </div>

      {!isSuper && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-3 rounded-lg text-sm" data-testid="admin-users-readonly">
          You can view this list but only Super Admins can invite or manage admins.
        </div>
      )}

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.id} className="bg-gray-800/50 border-sky-900/30" data-testid={`admin-row-${u.id}`}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${ROLE_BADGE[u.role] || ROLE_BADGE.admin}`}>
                    {(u.name || u.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">
                      {u.name || u.email}
                      {u.id === currentUser?.id && <span className="ml-2 text-xs text-sky-300">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={ROLE_BADGE[u.role] || ROLE_BADGE.admin}>
                    <ShieldCheck className="w-3 h-3 mr-1" />
                    {ROLES.find(r => r.value === u.role)?.label || u.role}
                  </Badge>
                  {u.has_pending_invite && (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                      <Clock className="w-3 h-3 mr-1" /> Invite pending
                    </Badge>
                  )}
                  {u.last_login && (
                    <span className="text-xs text-gray-500">last login {formatDate(u.last_login)}</span>
                  )}
                </div>

                {isSuper && (
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={u.role}
                      onChange={(e) => onRoleChange(u, e.target.value)}
                      disabled={busy === u.id}
                      className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1"
                      data-testid={`role-select-${u.id}`}
                    >
                      {ROLES.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
                    </select>
                    {u.has_pending_invite && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
                        disabled={busy === u.id}
                        onClick={() => onResend(u)}
                        data-testid={`resend-${u.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Resend
                      </Button>
                    )}
                    {u.has_pending_invite && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                        disabled={busy === u.id}
                        onClick={() => onCopyManualLink(u)}
                        data-testid={`copy-invite-link-${u.id}`}
                        title="Generate a fresh invite URL and copy it to the clipboard. Useful when email delivery isn't configured yet."
                      >
                        <Link2 className="w-4 h-4 mr-1" /> Copy Link
                      </Button>
                    )}
                    {u.id !== currentUser?.id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === u.id}
                        onClick={() => onDelete(u)}
                        data-testid={`delete-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
